// @file Vue composable that manages a Tiptap editor instance for text content in whiteboard shapes.

import { asciiSafeStringify } from '@@/bits/json_stringify'
import type { Editor as TiptapEditor } from '@padlet/whiteboard-tiptap'
import { createSharedComposable } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, shallowRef } from 'vue'

const WHITEBOARD_ELEMENT_TEXT_CONTENT_MODIFIER_ID = 'whiteboard-element-text-content-modifier'

const useWhiteboardElementTextContentModifier = createSharedComposable(
  (): {
    tiptapEditor: Ref<TiptapEditor | null>
    selectionRect: Ref<DOMRect | null>
    elementKey: Ref<string>
    getElement: () => HTMLElement | undefined
    showMenu: ({ editor, rect }: { editor: TiptapEditor; rect: DOMRect }) => void
    hideMenu: () => void
  } => {
    const tiptapEditor = shallowRef<TiptapEditor | null>(null)
    const selectionRect = shallowRef<DOMRect | null>(null)

    // We need to use a key to force rerender the element modifier when the selection rect changes.
    // This ensures the menu displays the correct active text formatting options for the current selection.
    // We can't use tiptapEditor because it doesn't update when the selection rect changes.
    const elementKey = computed(() => asciiSafeStringify(selectionRect.value))

    function getElement(): HTMLElement | undefined {
      return document.getElementById(WHITEBOARD_ELEMENT_TEXT_CONTENT_MODIFIER_ID) ?? undefined
    }

    function showMenu({ editor, rect }: { editor: TiptapEditor; rect: DOMRect }): void {
      tiptapEditor.value = editor
      selectionRect.value = rect
    }

    function hideMenu(): void {
      tiptapEditor.value = null
      selectionRect.value = null
    }

    return {
      tiptapEditor,
      selectionRect,
      elementKey,
      getElement,
      showMenu,
      hideMenu,
    }
  },
)

export { useWhiteboardElementTextContentModifier, WHITEBOARD_ELEMENT_TEXT_CONTENT_MODIFIER_ID }
